/**
 * 67.71 (the owner: "definitely want to change airstrike noise (I want it to sound much more like incoming artillery from
 * something like a ww1 or ww2 movie).  The whistle should be part of the actual in flight projectile then it sounds different
 * in the short leadup to striking"): a strike round's voice, the film's incoming shell, read from the sim's own round in
 * flight (the Match flies each as a bullet owned by its side's strike seat, core/airstrike.ts `airstrikeOwner`, its burst
 * `AIRSTRIKE.explosion`; a round flies about 4.4 s at human scale, ~100 people up at its top, coming down at ~100 people a
 * second). It replaces 67.70's whistle (kept on the sound sheet to compare).
 *   - **In flight:** from its last `seconds` (each round its own, 2–3 s) the round whistles where it is — the shriek falling
 *     in pitch as it comes down (the film's), bent by its closing speed (a share of the true Doppler: higher while it comes
 *     at you, dropping as it passes over to land beyond you), louder as it nears you, on its own side, the air's cut by
 *     distance, with the flutter of a tumbling shell.
 *   - **The last moments** (each round's `rush`, 0.3–0.8 s before its impact is heard): the rush of air tearing in — a
 *     roar swelling up to the impact, rising as it comes, a freight train's body under it — and a crack of air at the
 *     end; the whistle cuts out just before the burst. Landing very close the whistle stops as the rush starts and the
 *     rush is a hush: "you don't hear the one that gets you". Scheduled once, on the audio clock, at the heard impact
 *     (the round's time to the ground plus the sound's way from its landing), so it meets the burst's delay.
 *   - **The impact** is 67.54's heavy burst (the blast event's, unchanged) with `shellAftermath`: debris pattering down
 *     near it and a rolling rumble by distance, a far strike only a delayed boom and its roll (gunfire.ts `playShellAftermath`).
 *   - **Variety** (`shellVariety`, from the round's id): its whistle's length, pitch, fall, flutter and timbre, its rush's
 *     length and colour — a salvo is three shells, not one sample three times.
 * 67.81 (the owner: "better shell incoming sound"): two more styles on the sound sheet beside this one (`SHELL.style`, the
 * owner picked the scream, DECISIONS 259 — the default; `whistle` was 67.71's): **tear**, the air torn more than whistled — a lower, breathy
 * shriek, a ripping grit and a freight train's body swelling as it nears, its pitch held up while it comes at you and dropping
 * only as it passes over (more of the true Doppler, a shallow fall); and **scream**, the film's shriek made rougher — two
 * detuned reeds through a voice's two formants with a vibrato, falling late and steep. Their tables: `SHELL_STYLE`.
 * Louder by character, not gain: every part's peak sits under the burst's at its distance (the sheet's probe), through
 * the effects bus (the volume dial unchanged). Cost a frame: a pass over the bullets (a sign test each); per round in its
 * last seconds its height, the gravity on it, three distances and its voice's update — 11 nodes a round in flight, 9 more
 * for its last moments.
 */
import type { Bullet, GameState, PlanetState, Vec3 } from '../types';
import { AIRSTRIKE } from '../constants';
import { gravityAccel } from '../core/physics';
import { getSurfaceRadius } from '../core/surface';
import { personSetOf } from '../core/scaleProfile';
import { getAudioContext, getSfxBus, initAudio } from './sfx';

const METRES_PER_PERSON = 1.8, SPEED_OF_SOUND = 343;

/**
 * The voice's dials (people, seconds, Hz; ranges are a round's spread). Unplayed guesses (decision 121): the owner's ear decides.
 *   - `whistle`: heard over its last `seconds` within `range` people of the round; its pitch falls from `high` to `high`
 *     × `drop`; loud `gain` at `reach`; `doppler` the share of the true shift; `partial` a ringing overtone `ratio` up;
 *     `air` the breath of air over the shell (a band of noise at the pitch, `q` narrow); `flutter` Hz × `flutterDepth`.
 *   - `rush`: the last `seconds`, heard within `range` people of the landing, loud `gain` at `reach`, its colour round
 *     `hz`; within `hush` people of it the whistle is gone for the rush and the rush is quiet; a `crack` of air within `crackRange`.
 *   - `aftermath`: debris within `debrisRange` people (`debrisCount` bits, loud `debrisGain` at `debrisReach`); the rumble
 *     from `rumbleFrom` people, full at `rumbleFull`, `rumbleGain` × the burst's loudness, `rumbleSeconds` long.
 */
export const SHELL = {
  whistle: { seconds: [2, 3], high: [1500, 2300], drop: [0.42, 0.6], gain: 0.3, reach: 40, range: 500, doppler: 0.3, partial: [0.05, 0.18], ratio: [2.01, 2.76, 3.01], air: [0.3, 0.75], q: [6, 14], flutter: [5, 13], flutterDepth: [0.05, 0.22] },
  rush: { seconds: [0.3, 0.8], hz: [700, 1300], gain: 0.4, reach: 25, range: 160, hush: [4, 14], crack: 0.45, crackRange: 40 },
  aftermath: { debrisRange: 80, debrisCount: [8, 16], debrisGain: 0.4, debrisReach: 12, rumbleFrom: 40, rumbleFull: 300, rumbleGain: 1.6, rumbleSeconds: [1.8, 3.2] },
};

/** 67.81: an incoming shell's style (see the doc): `whistle` today's, `tear` and `scream` the sound sheet's candidates. */
export type ShellStyle = 'whistle' | 'tear' | 'scream';
export const SHELL_STYLES: readonly ShellStyle[] = ['whistle', 'tear', 'scream'];
/** The style in play: the scream (DECISIONS 259, the owner: *"Scream is best for shell sound"*); the whistle (67.71) and the tear stay on the sheet to compare. */
export const SHELL_STYLE_DEFAULT: ShellStyle = 'scream';
/**
 * Each style's shaping of the shared whistle (`whistle` exactly today's): the pitch × `pitch`, its fall `drop` ^ (k ^ `curve` × `fall`)
 * (k 0 → 1 over its last seconds), `doppler` the share of the true shift, × `gain`; the rush × `rush`; the freight body's
 * level at the ground × `body` (rising as k²), the grit's (a ripping texture) × `grit`.
 */
export const SHELL_STYLE: Record<ShellStyle, { pitch: number; fall: number; curve: number; doppler: number; gain: number; rush: number; body: number; grit: number }> = {
  whistle: { pitch: 1, fall: 1, curve: 0.85, doppler: 0.3, gain: 1, rush: 1, body: 0, grit: 0 },
  tear: { pitch: 0.55, fall: 0.45, curve: 1, doppler: 0.8, gain: 2.7, rush: 1.9, body: 1.1, grit: 0.7 },   // (gains matched to today's loudness on the sheet's probe)
  scream: { pitch: 0.8, fall: 1.25, curve: 1.7, doppler: 0.4, gain: 5.5, rush: 1.6, body: 0.35, grit: 0 },
};

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const smooth = (a: number, b: number, x: number) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
const lerp = (r: readonly number[], t: number) => r[0] + (r[1] - r[0]) * t;

/** A seeded stream (0 … 1) — the same round sounds the same each time it is heard. */
function stream(seed: number): () => number {
  let s = (Math.imul(seed | 0, 2654435761) ^ 0x9e3779b9) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), s | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** One round's own whistle and rush (see SHELL). */
export interface ShellVariety { seconds: number; high: number; drop: number; partial: number; ratio: number; air: number; q: number; flutter: number; flutterDepth: number; rush: number; rushHz: number }
export function shellVariety(id: number): ShellVariety {
  const r = stream(id), W = SHELL.whistle, R = SHELL.rush;
  return {
    seconds: lerp(W.seconds, r()), high: lerp(W.high, r()), drop: lerp(W.drop, r()), partial: lerp(W.partial, r()), ratio: W.ratio[Math.floor(r() * W.ratio.length) % W.ratio.length],
    air: lerp(W.air, r()), q: lerp(W.q, r()), flutter: lerp(W.flutter, r()), flutterDepth: lerp(W.flutterDepth, r()), rush: lerp(R.seconds, r()), rushHz: lerp(R.hz, r()),
  };
}

/** Is this bullet a strike's round (owned by a side's strike seat, its burst the strike's). */
export const isStrikeRound = (b: Pick<Bullet, 'ownerId' | 'explosion'>): boolean => b.ownerId < 0 && b.explosion === AIRSTRIKE.explosion;

/**
 * Seconds till a falling round meets the ground: `h` over it, `vr` its speed away from the centre (negative falling), `ar`
 * its acceleration away from the centre (negative: gravity) — the root of h + vr t + ½ ar t² = 0; Infinity when it is
 * still climbing away with nothing pulling it down.
 */
export function timeToGround(h: number, vr: number, ar: number): number {
  if (h <= 0) return 0;
  if (Math.abs(ar) < 1e-9) return vr < 0 ? h / -vr : Infinity;
  const disc = vr * vr - 2 * ar * h;
  if (disc < 0) return Infinity;
  const t = (-vr - Math.sqrt(disc)) / ar;
  return t > 0 ? t : Infinity;
}

/** A round's height over the ground (world units), its speed and acceleration away from its planet's centre, that way (up), and the planet (the nearest). */
export function roundFall(state: Pick<GameState, 'planets' | 'config'>, b: Pick<Bullet, 'pos' | 'vel' | 'gravity'>): { h: number; vr: number; ar: number; up: Vec3; planet: PlanetState } | null {
  let planet: PlanetState | null = null, best = Infinity;
  for (const p of state.planets) { const d = Math.hypot(b.pos.x - p.position.x, b.pos.y - p.position.y, b.pos.z - p.position.z) - p.radius; if (d < best) { best = d; planet = p; } }
  if (!planet) return null;
  const rx = b.pos.x - planet.position.x, ry = b.pos.y - planet.position.y, rz = b.pos.z - planet.position.z, r = Math.hypot(rx, ry, rz);
  const up = { x: rx / r, y: ry / r, z: rz / r };
  const ps = personSetOf(state as GameState), g = gravityAccel(b.pos, state.planets as PlanetState[]), fall = (b.gravity ?? ps.bulletGravityScale) * ps.gravityScale;
  return { h: r - getSurfaceRadius(planet, up), vr: b.vel.x * up.x + b.vel.y * up.y + b.vel.z * up.z, ar: (g.x * up.x + g.y * up.y + g.z * up.z) * fall, up, planet };
}

/** A round as the ear has it this frame: its time to the ground, how far it is (people) and on which side, how fast it closes (people a second), and its landing's distance, side and the sound's way from there (s). */
export interface ShellHeardFrom { tti: number; people: number; pan: number; closing: number; landPeople: number; landPan: number; delay: number }
/** The ear: a place's distance (people), side, and (optional, else by its distance) the seconds its sound takes to arrive. */
export type ShellEar = (p: Vec3) => { people: number; pan: number; delay?: number };

/** Strike round `b` from the ear, or null while it is not yet in any round's last seconds. */
export function observeRound(state: Pick<GameState, 'planets' | 'config'>, b: Pick<Bullet, 'pos' | 'vel' | 'gravity'>, ear: ShellEar): ShellHeardFrom | null {
  const f = roundFall(state, b);
  if (!f) return null;
  let tti = timeToGround(f.h, f.vr, f.ar);
  if (!(tti <= SHELL.whistle.seconds[1] + 0.5)) return null;
  // its landing: on its way, bent down by the pull on it — then once more against the ground there (not the ground under it)
  const landAt = (s: number) => { const by = 0.5 * f.ar * s * s; return { x: b.pos.x + b.vel.x * s + f.up.x * by, y: b.pos.y + b.vel.y * s + f.up.y * by, z: b.pos.z + b.vel.z * s + f.up.z * by }; };
  const p = landAt(tti), c = f.planet.position, d = { x: p.x - c.x, y: p.y - c.y, z: p.z - c.z }, dl = Math.hypot(d.x, d.y, d.z);
  if (dl > 0) { const r = Math.hypot(b.pos.x - c.x, b.pos.y - c.y, b.pos.z - c.z); tti = timeToGround(r - getSurfaceRadius(f.planet, { x: d.x / dl, y: d.y / dl, z: d.z / dl }), f.vr, f.ar); }
  if (!(tti <= SHELL.whistle.seconds[1])) return null;
  const at = ear(b.pos), dt = 0.02, ahead = ear({ x: b.pos.x + b.vel.x * dt, y: b.pos.y + b.vel.y * dt, z: b.pos.z + b.vel.z * dt });
  const land = ear(landAt(tti));
  return { tti, people: at.people, pan: at.pan, closing: (at.people - ahead.people) / dt, landPeople: land.people, landPan: land.pan, delay: land.delay ?? (land.people * METRES_PER_PERSON) / SPEED_OF_SOUND };
}

export interface WhistleHeard { gain: number; hz: number; pan: number; cutoff: number; /** 67.81: how far into its last seconds (0 … 1: at the ground) */ k: number }

/** Round `v`'s whistle `tti` seconds from the ground, `people` from the ear on its `pan` side, closing at `closing` people a second; null before its last seconds or past its range. */
export function hearShellWhistle(v: ShellVariety, tti: number, people: number, pan: number, closing: number, style: ShellStyle = SHELL_STYLE_DEFAULT): WhistleHeard | null {
  const W = SHELL.whistle, S = SHELL_STYLE[style];
  if (!(tti <= v.seconds) || tti < 0 || people > W.range) return null;
  const k = 1 - tti / v.seconds;   // 0 as it starts, 1 at the ground
  const towards = clamp(closing * METRES_PER_PERSON, -300, 300);
  const doppler = style === 'whistle' ? clamp(1 + W.doppler * (SPEED_OF_SOUND / (SPEED_OF_SOUND - towards) - 1), 0.8, 1.35)
    : clamp(1 + S.doppler * (SPEED_OF_SOUND / (SPEED_OF_SOUND - towards) - 1), 0.7, 1.6);
  return {
    gain: W.gain * S.gain * Math.min(1, k / 0.12) * (0.15 + 0.85 * k * k) / (1 + Math.max(0, people) / W.reach),
    hz: v.high * S.pitch * Math.pow(v.drop, Math.pow(k, S.curve) * S.fall) * doppler,
    pan: clamp(pan, -1, 1),
    cutoff: clamp(9000 * Math.exp(-Math.max(0, people) / 150), 400, 9000),
    k,
  };
}

/** Round `v`'s last moments, landing `landPeople` from the ear: how long before the heard impact the whistle cuts, the rush's loudness and the crack's; 0 past their ranges. */
export function shellFinale(v: ShellVariety, landPeople: number): { cutLead: number; rush: number; crack: number; cutoff: number } {
  const R = SHELL.rush, d = Math.max(0, landPeople), hush = smooth(R.hush[0], R.hush[1], d);
  return {
    cutLead: v.rush + (0.04 - v.rush) * hush,   // very close: gone as the rush starts; else just before the burst
    rush: d > R.range ? 0 : R.gain * (0.3 + 0.7 * hush) * (1 - smooth(R.range * 0.6, R.range, d)) / (1 + d / R.reach),
    crack: d > R.crackRange ? 0 : R.crack * hush * (1 - d / R.crackRange),
    cutoff: clamp(9000 * Math.exp(-d / 120), 500, 9000),
  };
}

/** A strike round's burst heard `people` away, beside the burst: the debris pattering down (loudness, bits) and the rolling rumble (× the burst's loudness, seconds). */
export interface ShellAftermath { debris: number; bits: number; rumble: number; seconds: number }
export function shellAftermath(people: number, seed = 0): ShellAftermath {
  const A = SHELL.aftermath, d = Math.max(0, people), r = stream(seed + 7);
  return {
    debris: d > A.debrisRange ? 0 : A.debrisGain * (1 - smooth(A.debrisRange * 0.5, A.debrisRange, d)) / (1 + d / A.debrisReach),
    bits: Math.round(lerp(A.debrisCount, r())),
    rumble: A.rumbleGain * smooth(A.rumbleFrom, A.rumbleFull, d),
    seconds: lerp(A.rumbleSeconds, r()),
  };
}

export interface ShellOut { ctx: BaseAudioContext; dest: AudioNode }
interface Voice {
  out: GainNode; gate: GainNode; air: BiquadFilterNode; side: StereoPannerNode; tone: OscillatorNode; partial: OscillatorNode; band: BiquadFilterNode; ratio: number; outs: GainNode[]; sources: AudioScheduledSourceNode[];
  /** 67.81's styles: the freight body's and the grit's levels (tear, scream), the second formant at `formantRatio` × the pitch and the vibrato's depth (scream) */
  body?: GainNode; grit?: GainNode; formant?: BiquadFilterNode; formantRatio?: number; vibrato?: GainNode;
}
interface Entry { v: Voice | null; variety: ShellVariety; /** the audio time its impact is heard (its last moments scheduled), else null */ hit: number | null }

const noiseBufs = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBufs.get(ctx);
  if (!b) {
    const len = ctx.sampleRate * 2; b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBufs.set(ctx, b);
  }
  return b;
}

/** Seconds before its heard impact a round's last moments are scheduled (a frame's margin over its rush). */
const FINALE_LEAD = 0.12;

/** The strike rounds' voices in play: `frame` once a frame with the state and the ear. */
export class ShellVoices {
  private readonly entries = new Map<number, Entry>();
  /** × every shell's loudness. */
  volume = 1;
  /** 67.81: the style of the voices made from now on (the sound sheet switches it; play keeps the default). */
  style: ShellStyle = SHELL_STYLE_DEFAULT;
  constructor(private readonly out?: ShellOut) {}

  /** Shells sounding now (in flight, or their last moments still playing). */
  get live(): number {
    const now = this.out?.ctx.currentTime ?? getAudioContext()?.currentTime ?? 0;
    let n = 0;
    for (const e of this.entries.values()) if (e.v && (e.hit === null || now < e.hit + 0.2)) n++;
    return n;
  }

  frame(state: Pick<GameState, 'bullets' | 'planets' | 'config'>, ear: ShellEar, muted: boolean, _dt: number): void {
    if (muted) { this.stop(); return; }
    const seen = new Set<number>();
    for (const b of state.bullets ?? []) {
      if (!isStrikeRound(b)) continue;
      seen.add(b.id);
      this.drive(b.id, observeRound(state, b, ear));
    }
    for (const id of [...this.entries.keys()]) if (!seen.has(id)) this.end(id);
  }

  /** Round `id` this frame as the ear has it (null: not yet in its last seconds). The sheet drives it directly. */
  drive(id: number, o: ShellHeardFrom | null): void {
    let e = this.entries.get(id);
    if (!e) { if (!o) return; e = { v: null, variety: shellVariety(id), hit: null }; this.entries.set(id, e); }
    if (e.hit !== null) return;   // its last moments are on the audio clock
    const h = o && hearShellWhistle(e.variety, o.tti, o.people, o.pan, o.closing, this.style);
    if (!o || !h) { if (e.v) { this.release(e.v, 0.06); e.v = null; } return; }
    const io = this.where();
    if (!io) return;
    e.v ??= this.voice(io.ctx, io.dest, h, e.variety, this.style);
    const t = io.ctx.currentTime;
    this.set(e.v, h, t);
    if (o.tti + o.delay <= e.variety.rush + FINALE_LEAD) { e.hit = t + o.tti + o.delay; this.finale(io, e.v, e.variety, o, e.hit); }
  }

  /** Round `id` is down (left the state): a whistle with its last moments scheduled plays them out; any other stops at once. */
  end(id: number): void {
    const e = this.entries.get(id);
    if (!e) return;
    const now = e.v?.out.context.currentTime ?? 0;
    if (e.hit !== null && e.v && now < e.hit + 0.2) return;
    if (e.v && e.hit === null) this.release(e.v, 0.03);
    this.entries.delete(id);
  }

  stop(): void { for (const e of this.entries.values()) if (e.v) this.release(e.v, 0.03); this.entries.clear(); }

  private set(v: Voice, h: WhistleHeard, t: number): void {
    const tau = 0.03;
    v.tone.frequency.setTargetAtTime(h.hz, t, tau);
    v.partial.frequency.setTargetAtTime(h.hz * v.ratio, t, tau);
    v.band.frequency.setTargetAtTime(h.hz, t, tau);
    v.air.frequency.setTargetAtTime(h.cutoff, t, tau);
    v.side.pan.setTargetAtTime(h.pan, t, tau);
    v.out.gain.setTargetAtTime(h.gain * this.volume, t, tau);
    // 67.81's styles: the body swelling as it nears, the grit with it; the scream's second formant and its vibrato's depth
    const S = SHELL_STYLE[this.style];
    if (v.body) v.body.gain.setTargetAtTime(S.body * h.k * h.k, t, tau);
    if (v.grit) v.grit.gain.setTargetAtTime(S.grit * (0.2 + 0.8 * h.k), t, tau);
    if (v.formant) v.formant.frequency.setTargetAtTime(h.hz * (v.formantRatio ?? 2.3), t, tau);
    if (v.vibrato) v.vibrato.gain.setTargetAtTime(h.hz * 0.018, t, tau);
  }

  /** The last moments, at `hit` (the heard impact): the whistle on to its cut, the rush swelling to the burst, the crack. */
  private finale({ ctx, dest }: ShellOut, v: Voice, s: ShellVariety, o: ShellHeardFrom, hit: number): void {
    const style = this.style, S = SHELL_STYLE[style];
    const f0 = shellFinale(s, o.landPeople), f = { ...f0, rush: f0.rush * S.rush }, now = ctx.currentTime, cut = Math.max(now + 0.02, hit - f.cutLead);
    // the whistle: its fall carried on to the cut (no more frames drive it), then gone — sharply when it is the one that gets you
    const last = hearShellWhistle(s, Math.max(0, Math.min(s.seconds, f.cutLead)), o.landPeople, o.landPan, o.closing, style);
    const ramp = (p: AudioParam, to: number) => { p.cancelScheduledValues(now); p.setValueAtTime(p.value, now); p.linearRampToValueAtTime(to, cut); };
    if (last) { ramp(v.tone.frequency, last.hz); ramp(v.partial.frequency, last.hz * v.ratio); ramp(v.band.frequency, last.hz); ramp(v.out.gain, last.gain * this.volume); if (v.formant) ramp(v.formant.frequency, last.hz * (v.formantRatio ?? 2.3)); if (v.body) ramp(v.body.gain, S.body); }
    const fade = f.cutLead > 0.1 ? 0.025 : 0.05;
    v.gate.gain.setValueAtTime(1, Math.max(now, cut - fade)); v.gate.gain.linearRampToValueAtTime(0, cut);
    for (const src of v.sources) { try { src.stop(cut + 0.1); } catch { /* stopped */ } }
    if (f.rush <= 0 && f.crack <= 0) return;
    // the rush: tearing air rising to the impact over a freight train's body, from the landing's side, cut by its distance
    const from = Math.max(now, hit - s.rush), end = hit + 0.02;
    const rushOut = ctx.createGain(); rushOut.gain.value = 0;
    const air = ctx.createBiquadFilter(); air.type = 'lowpass'; air.frequency.value = f.cutoff; air.Q.value = 0.5;
    const side = ctx.createStereoPanner(); side.pan.value = clamp(o.landPan, -1, 1);
    rushOut.connect(air); air.connect(side); side.connect(dest);
    const tear = ctx.createBufferSource(); tear.buffer = noiseBuf(ctx); tear.loop = true;
    const tearBand = ctx.createBiquadFilter(); tearBand.type = 'bandpass'; tearBand.Q.value = 0.9;
    tearBand.frequency.setValueAtTime(s.rushHz * 0.5, from); tearBand.frequency.exponentialRampToValueAtTime(s.rushHz * 1.6, hit);
    const body = ctx.createBufferSource(); body.buffer = noiseBuf(ctx); body.loop = true;
    const bodyLow = ctx.createBiquadFilter(); bodyLow.type = 'lowpass'; bodyLow.frequency.value = 220; bodyLow.Q.value = 0.7;
    const bodyGain = ctx.createGain(); bodyGain.gain.value = style === 'tear' ? 1.1 : 0.7;   // (67.81: the tear's freight train heavier)
    tear.connect(tearBand); tearBand.connect(rushOut); body.connect(bodyLow); bodyLow.connect(bodyGain); bodyGain.connect(rushOut);
    const g = rushOut.gain;
    g.setValueAtTime(0.0001, from); g.exponentialRampToValueAtTime(Math.max(0.0002, f.rush * this.volume), hit - 0.012); g.linearRampToValueAtTime(0, end);
    // the crack of air, just before the burst
    const crack = ctx.createBufferSource(); crack.buffer = noiseBuf(ctx);
    const crackHi = ctx.createBiquadFilter(); crackHi.type = 'highpass'; crackHi.frequency.value = 2200;
    const crackGain = ctx.createGain(); crackGain.gain.value = 0;
    crack.connect(crackHi); crackHi.connect(crackGain); crackGain.connect(air);
    const ct = Math.max(from, hit - 0.04);
    crackGain.gain.setValueAtTime(0, ct); crackGain.gain.linearRampToValueAtTime(f.crack * this.volume, ct + 0.002); crackGain.gain.exponentialRampToValueAtTime(0.0005, ct + 0.025);
    tear.start(from, Math.random()); body.start(from, Math.random()); crack.start(ct, Math.random(), 0.06);
    tear.stop(end + 0.05); body.stop(end + 0.05);
    v.outs.push(rushOut, crackGain); v.sources.push(tear, body, crack);
    tear.onended = () => { for (const n of [rushOut, air, side, tear, tearBand, body, bodyLow, bodyGain, crack, crackHi, crackGain]) n.disconnect(); };
  }

  private release(v: Voice, tau: number): void {
    const t = v.out.context.currentTime;
    for (const g of [v.out, ...v.outs]) { g.gain.cancelScheduledValues(t); g.gain.setTargetAtTime(0, t, tau); }
    for (const s of v.sources) { try { s.stop(t + tau * 8); } catch { /* already stopped */ } }
  }

  private voice(ctx: BaseAudioContext, dest: AudioNode, h: WhistleHeard, s: ShellVariety, style: ShellStyle = 'whistle'): Voice {
    if (style !== 'whistle') return this.styledVoice(ctx, dest, h, s, style);
    const sources: AudioScheduledSourceNode[] = [];
    const out = ctx.createGain(); out.gain.value = 0;
    const gate = ctx.createGain(); gate.gain.value = 1;
    const air = ctx.createBiquadFilter(); air.type = 'lowpass'; air.frequency.value = h.cutoff; air.Q.value = 0.5;
    const side = ctx.createStereoPanner(); side.pan.value = h.pan;
    // the tumbling shell's flutter: the whistle's loudness wobbling at its own rate
    const trem = ctx.createGain(); trem.gain.value = 1 - s.flutterDepth;
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = s.flutter;
    const depth = ctx.createGain(); depth.gain.value = s.flutterDepth;
    lfo.connect(depth); depth.connect(trem.gain);
    trem.connect(air); air.connect(side); side.connect(gate); gate.connect(out); out.connect(dest);
    const tone = ctx.createOscillator(); tone.type = 'sine'; tone.frequency.value = h.hz;
    const partial = ctx.createOscillator(); partial.type = 'sine'; partial.frequency.value = h.hz * s.ratio;
    const pg = ctx.createGain(); pg.gain.value = s.partial;
    tone.connect(trem); partial.connect(pg); pg.connect(trem);
    const noise = ctx.createBufferSource(); noise.buffer = noiseBuf(ctx); noise.loop = true;
    const band = ctx.createBiquadFilter(); band.type = 'bandpass'; band.frequency.value = h.hz; band.Q.value = s.q;
    const ng = ctx.createGain(); ng.gain.value = s.air;
    noise.connect(band); band.connect(ng); ng.connect(trem);
    for (const src of [tone, partial, noise, lfo]) { src.start(); sources.push(src); }
    noise.onended = () => { for (const n of [out, gate, air, side, trem, lfo, depth, pg, band, ng, tone, partial, noise]) n.disconnect(); };
    return { out, gate, air, side, tone, partial, band, ratio: s.ratio, outs: [], sources };
  }

  /**
   * 67.81's candidates. **tear:** the band of air the voice (a wide band of noise at the pitch, a quiet sine for its centre),
   * a ripping grit (high noise chopped at ~2.6× the flutter) and a freight body (low noise) swelling with its closeness.
   * **scream:** two detuned sawtooth reeds through a formant at the pitch and a second higher (× 2.3), a vibrato on both,
   * the breath of air under them and a little body. Both share today's chain: the tumble's flutter, the air's cut, the side,
   * the gate, the loudness.
   */
  private styledVoice(ctx: BaseAudioContext, dest: AudioNode, h: WhistleHeard, s: ShellVariety, style: 'tear' | 'scream'): Voice {
    const sources: AudioScheduledSourceNode[] = [], nodes: AudioNode[] = [];
    const mk = <T extends AudioNode>(n: T): T => { nodes.push(n); return n; };
    const out = mk(ctx.createGain()); out.gain.value = 0;
    const gate = mk(ctx.createGain()); gate.gain.value = 1;
    const air = mk(ctx.createBiquadFilter()); air.type = 'lowpass'; air.frequency.value = h.cutoff; air.Q.value = 0.5;
    const side = mk(ctx.createStereoPanner()); side.pan.value = h.pan;
    const trem = mk(ctx.createGain()); trem.gain.value = 1 - s.flutterDepth;
    const lfo = mk(ctx.createOscillator()); lfo.type = 'sine'; lfo.frequency.value = s.flutter;
    const depth = mk(ctx.createGain()); depth.gain.value = s.flutterDepth;
    lfo.connect(depth); depth.connect(trem.gain);
    trem.connect(air); air.connect(side); side.connect(gate); gate.connect(out); out.connect(dest);
    const noise = mk(ctx.createBufferSource()); noise.buffer = noiseBuf(ctx); noise.loop = true;
    const S = SHELL_STYLE[style];
    // the freight body: low noise under it all, its level swelling as it nears (set by the frame)
    const bodyLow = mk(ctx.createBiquadFilter()); bodyLow.type = 'lowpass'; bodyLow.frequency.value = 170; bodyLow.Q.value = 0.8;
    const body = mk(ctx.createGain()); body.gain.value = S.body * h.k * h.k;
    noise.connect(bodyLow); bodyLow.connect(body); body.connect(air);
    let tone: OscillatorNode, partial: OscillatorNode, band: BiquadFilterNode, ratio: number, grit: GainNode | undefined, formant: BiquadFilterNode | undefined, vibrato: GainNode | undefined;
    if (style === 'tear') {
      band = mk(ctx.createBiquadFilter()); band.type = 'bandpass'; band.frequency.value = h.hz; band.Q.value = Math.max(1.5, s.q * 0.3);
      const bg = mk(ctx.createGain()); bg.gain.value = 1.6;
      noise.connect(band); band.connect(bg); bg.connect(trem);
      tone = mk(ctx.createOscillator()); tone.type = 'sine'; tone.frequency.value = h.hz;
      const tg = mk(ctx.createGain()); tg.gain.value = 0.16;
      tone.connect(tg); tg.connect(trem);
      ratio = s.ratio; partial = mk(ctx.createOscillator()); partial.type = 'sine'; partial.frequency.value = h.hz * ratio;
      const pg = mk(ctx.createGain()); pg.gain.value = s.partial * 0.4;
      partial.connect(pg); pg.connect(trem);
      // the grit: high noise chopped by a square at ~2.6× the flutter — the tearing
      const hi = mk(ctx.createBiquadFilter()); hi.type = 'highpass'; hi.frequency.value = 2600;
      const chop = mk(ctx.createGain()); chop.gain.value = 0.5;
      const chopLfo = mk(ctx.createOscillator()); chopLfo.type = 'square'; chopLfo.frequency.value = s.flutter * 2.6;
      const chopDepth = mk(ctx.createGain()); chopDepth.gain.value = 0.5;
      chopLfo.connect(chopDepth); chopDepth.connect(chop.gain);
      grit = mk(ctx.createGain()); grit.gain.value = S.grit * (0.2 + 0.8 * h.k);
      noise.connect(hi); hi.connect(chop); chop.connect(grit); grit.connect(trem);
      sources.push(chopLfo);
    } else {
      // two reeds a little apart (the partial's ratio the detune, ~14 cents), through two formants, with a vibrato
      ratio = Math.pow(2, 14 / 1200);
      tone = mk(ctx.createOscillator()); tone.type = 'sawtooth'; tone.frequency.value = h.hz;
      partial = mk(ctx.createOscillator()); partial.type = 'sawtooth'; partial.frequency.value = h.hz * ratio;
      const reeds = mk(ctx.createGain()); reeds.gain.value = 0.22;
      tone.connect(reeds); partial.connect(reeds);
      band = mk(ctx.createBiquadFilter()); band.type = 'bandpass'; band.frequency.value = h.hz; band.Q.value = 7;
      formant = mk(ctx.createBiquadFilter()); formant.type = 'bandpass'; formant.frequency.value = h.hz * 2.3; formant.Q.value = 9;
      const fg = mk(ctx.createGain()); fg.gain.value = 0.45;
      reeds.connect(band); band.connect(trem); reeds.connect(formant); formant.connect(fg); fg.connect(trem);
      const vib = mk(ctx.createOscillator()); vib.type = 'sine'; vib.frequency.value = 5 + s.flutter * 0.25;
      vibrato = mk(ctx.createGain()); vibrato.gain.value = h.hz * 0.018;
      vib.connect(vibrato); vibrato.connect(tone.frequency); vibrato.connect(partial.frequency);
      const breath = mk(ctx.createBiquadFilter()); breath.type = 'bandpass'; breath.frequency.value = h.hz * 1.5; breath.Q.value = 2;
      const brg = mk(ctx.createGain()); brg.gain.value = s.air * 0.5;
      noise.connect(breath); breath.connect(brg); brg.connect(trem);
      sources.push(vib);
    }
    for (const src of [tone, partial, noise, lfo]) { src.start(); sources.push(src); }
    for (const src of sources) if (src !== tone && src !== partial && src !== noise && src !== lfo) src.start();
    noise.onended = () => { for (const n of nodes) n.disconnect(); };
    return { out, gate, air, side, tone, partial, band, ratio, outs: [], sources, body, ...(grit ? { grit } : {}), ...(formant ? { formant, formantRatio: 2.3 } : {}), ...(vibrato ? { vibrato } : {}) };
  }

  private where(): ShellOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }
}
